// Card del Home del Portal Vendedor — equivalente 1:1 de `.home-card` del mock
// `portal-vendedor.html`: cabecera densa con título en MAYÚSCULAS + slot `meta` a la derecha
// (normalmente el enlace "Ver todas →"), y cuerpo con scroll propio.
//
// Es una pieza del Home, no del kit: el kit `Card` tiene cabecera de tipografía normal y padding
// más generoso, y el Home del vendedor es deliberadamente denso (pantalla de tienda, tablet).
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export function HomeCard({
    title,
    meta,
    bodyClassName,
    className,
    children,
}: {
    title: string;
    /** Contenido a la derecha de la cabecera: enlace "Ver todas →", contador, etc. */
    meta?: ReactNode;
    bodyClassName?: string;
    className?: string;
    children: ReactNode;
}) {
    return (
        <div
            className={cn(
                'flex min-h-0 flex-col overflow-hidden rounded-md border border-border bg-card',
                className,
            )}
        >
            <div className="flex flex-none items-center justify-between gap-2 border-b border-border bg-muted/30 px-2.5 py-1.5">
                <span className="text-[11.5px] font-semibold uppercase tracking-[0.04em]">
                    {title}
                </span>
                {meta ? <span className="text-[10.5px] text-muted-foreground">{meta}</span> : null}
            </div>
            <div className={cn('min-h-0 overflow-y-auto py-1', bodyClassName)}>{children}</div>
        </div>
    );
}

/** Fila densa del Home (`.row-dense` del mock): hora/id · nombre · detalle · chip · monto.
 *  Se renderiza como <button> cuando es clicable, para que sea navegable con teclado.
 *  El aire lateral por defecto es el del Home (10 px); «Mi tienda» lo pisa con `className`, porque
 *  su mock pide 12 px. */
export function HomeRow({
    onClick,
    className: extra,
    children,
}: {
    onClick?: () => void;
    className?: string;
    children: ReactNode;
}) {
    const className = cn(
        'flex w-full items-center gap-2 border-b border-border/50 px-2.5 py-[5px] text-left text-[11.5px] last:border-b-0',
        onClick && 'cursor-pointer hover:bg-accent/40',
        extra,
    );
    if (!onClick) return <div className={className}>{children}</div>;
    return (
        <button type="button" className={className} onClick={onClick}>
            {children}
        </button>
    );
}

/** Vacío inline de una card del Home — el mock usa una línea en itálica, no un EmptyState. */
export function HomeEmpty({ children }: { children: ReactNode }) {
    return (
        <div className="p-3 text-center text-[11px] italic text-muted-foreground">{children}</div>
    );
}
